import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Athletes from "./pages/Athletes";
import AthleteDetail from "./pages/AthleteDetail";
import SubmitDeal from "./pages/SubmitDeal";
import MyDeals from "./pages/MyDeals";
import PendingDeals from "./pages/PendingDeals";
import DealDetail from "./pages/DealDetail";
import Settings from "./pages/Settings";
import AssociatedEntities from "./pages/AssociatedEntities";
import InstitutionalSponsors from "./pages/InstitutionalSponsors";
import AuditLog from "./pages/AuditLog";
import Analytics from "./pages/Analytics";
import Reports from "./pages/Reports";
import RevenueSharingPools from "./pages/RevenueSharingPools";
import AthleteCompensation from "./pages/AthleteCompensation";
import TitleIXCompliance from "./pages/TitleIXCompliance";
import CAPSManagement from "./pages/CAPSManagement";
import Messages from "./pages/Messages";
import Pricing from "./pages/Pricing";
import Education from "./pages/Education";
import Resources from "./pages/Resources";
import Stats from "./pages/Stats";
import Compliance from "./pages/Compliance";
import NotFound from "./pages/NotFound";
import SetupDemo from "./pages/SetupDemo";
import AthletePortal from "./pages/AthletePortal";
import AthleteDeals from "./pages/AthleteDeals";
import AthleteDealSubmit from "./pages/AthleteDealSubmit";
import HelpCenter from "./pages/HelpCenter";
import ComplianceOfficerSOP from "./pages/help/ComplianceOfficerSOP";
import AthleteSOP from "./pages/help/AthleteSOP";
import AthleticDirectorSOP from "./pages/help/AthleticDirectorSOP";
import DealManagementGuide from "./pages/help/DealManagementGuide";
import Profile from "./pages/Profile";
import FirmPortal from "./pages/firm/FirmPortal";
import CrossInstitutionDashboard from "./pages/firm/CrossInstitutionDashboard";
import BrandRegistration from "./pages/brand/BrandRegistration";
import BrandPortal from "./pages/brand/BrandPortal";
import AthleteMarketplace from "./pages/brand/AthleteMarketplace";
import CollectivePortal from "./pages/collective/CollectivePortal";
import ContractTemplates from "./pages/collective/ContractTemplates";
import TaxDocumentCenter from "./pages/collective/TaxDocumentCenter";
import Donors from "./pages/collective/Donors";
import DonorProfile from "./pages/collective/DonorProfile";
import Contributions from "./pages/collective/Contributions";
import AgencyPortal from "./pages/agency/AgencyPortal";
import UserManagement from "./pages/UserManagement";
import ComplianceChecklist from "./pages/ComplianceChecklist";
import AIShowcase from "./pages/AIShowcase";
import DemoWorkflow from "./pages/DemoWorkflow";
import WorkflowDocs from "./pages/WorkflowDocs";
import Eligibility from "./pages/Eligibility";
import FinancialAid from "./pages/FinancialAid";
import TimeTracking from "./pages/TimeTracking";
import Recruitment from "./pages/Recruitment";
import ApiManagement from "./pages/ApiManagement";
import WebhookManagement from "./pages/WebhookManagement";
import ApiDocs from "./pages/ApiDocs";
import NILGoExport from "./pages/NILGoExport";
import TaxDashboard from "./pages/athlete/TaxDashboard";
import RuleChangeManagement from "./pages/admin/RuleChangeManagement";
import ProspectDisclosure from "./pages/public/ProspectDisclosure";
import FMVMarketRates from "./pages/FMVMarketRates";
import DealPipeline from "./pages/DealPipeline";
import Renewals from "./pages/Renewals";
import AgentMarketplace from "./pages/AgentMarketplace";
import AgentRegistry from "./pages/admin/AgentRegistry";
import BrandMarketplace from "./pages/brand/BrandMarketplace";
import MonetizationHub from "./pages/athlete/MonetizationHub";
import ConferencePartnership from "./pages/admin/ConferencePartnership";
import ReferralPartnerApplication from "./pages/public/ReferralPartnerApplication";
import ReferralPartnerPortal from "./pages/referral/ReferralPartnerPortal";
import ReferralPartnerManagement from "./pages/admin/ReferralPartnerManagement";
import ReferralAnalytics from "./pages/admin/ReferralAnalytics";
import IntegrationHealth from "./pages/admin/IntegrationHealth";
import OAuthConsent from "./pages/OAuthConsent";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard/compliance" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><Compliance /></ProtectedRoute>} />
          <Route path="/dashboard/athletes" element={<ProtectedRoute><Athletes /></ProtectedRoute>} />
          <Route path="/dashboard/athletes/:id" element={<ProtectedRoute><AthleteDetail /></ProtectedRoute>} />
          <Route path="/dashboard/deals" element={<ProtectedRoute><MyDeals /></ProtectedRoute>} />
          <Route path="/dashboard/deals/submit" element={<ProtectedRoute><SubmitDeal /></ProtectedRoute>} />
          <Route path="/dashboard/deals/pending" element={<ProtectedRoute><PendingDeals /></ProtectedRoute>} />
          <Route path="/dashboard/deals/:id" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
          <Route path="/dashboard/settings" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><Settings /></ProtectedRoute>} />
          <Route path="/dashboard/admin/users" element={<ProtectedRoute requiredRoles={["super_admin"]}><UserManagement /></ProtectedRoute>} />
          <Route path="/dashboard/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/dashboard/entities" element={<ProtectedRoute><AssociatedEntities /></ProtectedRoute>} />
          <Route path="/dashboard/sponsors" element={<ProtectedRoute><InstitutionalSponsors /></ProtectedRoute>} />
          <Route path="/dashboard/audit" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><AuditLog /></ProtectedRoute>} />
          <Route path="/dashboard/analytics" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><Analytics /></ProtectedRoute>} />
          <Route path="/dashboard/reports" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><Reports /></ProtectedRoute>} />
          <Route path="/dashboard/caps" element={<ProtectedRoute requiredRoles={["super_admin", "athletic_director"]}><CAPSManagement /></ProtectedRoute>} />
          <Route path="/dashboard/caps/pools" element={<ProtectedRoute requiredRoles={["super_admin", "athletic_director"]}><RevenueSharingPools /></ProtectedRoute>} />
          <Route path="/dashboard/caps/compensation" element={<ProtectedRoute requiredRoles={["super_admin", "athletic_director"]}><AthleteCompensation /></ProtectedRoute>} />
          <Route path="/dashboard/caps/title-ix" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><TitleIXCompliance /></ProtectedRoute>} />
          <Route path="/dashboard/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/education" element={<Education />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/setup-demo" element={<SetupDemo />} />
          <Route path="/athlete/portal" element={<ProtectedRoute><AthletePortal /></ProtectedRoute>} />
          <Route path="/athlete/deals" element={<ProtectedRoute><AthleteDeals /></ProtectedRoute>} />
          <Route path="/athlete/deals/submit" element={<ProtectedRoute><AthleteDealSubmit /></ProtectedRoute>} />
          <Route path="/athlete/deals/:id" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
          <Route path="/dashboard/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
          <Route path="/dashboard/help/sop/compliance-officer" element={<ProtectedRoute><ComplianceOfficerSOP /></ProtectedRoute>} />
          <Route path="/dashboard/help/sop/athlete" element={<ProtectedRoute><AthleteSOP /></ProtectedRoute>} />
          <Route path="/dashboard/help/sop/athletic-director" element={<ProtectedRoute><AthleticDirectorSOP /></ProtectedRoute>} />
          <Route path="/dashboard/help/feature/deal-management" element={<ProtectedRoute><DealManagementGuide /></ProtectedRoute>} />
          <Route path="/firm/portal" element={<ProtectedRoute><FirmPortal /></ProtectedRoute>} />
          <Route path="/firm/cross-institution-dashboard" element={<ProtectedRoute><CrossInstitutionDashboard /></ProtectedRoute>} />
          <Route path="/brand/register" element={<ProtectedRoute><BrandRegistration /></ProtectedRoute>} />
          <Route path="/brand/portal" element={<ProtectedRoute><BrandPortal /></ProtectedRoute>} />
          <Route path="/brand/marketplace" element={<ProtectedRoute><AthleteMarketplace /></ProtectedRoute>} />
          <Route path="/collective/portal" element={<ProtectedRoute><CollectivePortal /></ProtectedRoute>} />
          <Route path="/collective/donors" element={<ProtectedRoute><Donors /></ProtectedRoute>} />
          <Route path="/collective/donors/:id" element={<ProtectedRoute><DonorProfile /></ProtectedRoute>} />
          <Route path="/collective/contributions" element={<ProtectedRoute><Contributions /></ProtectedRoute>} />
          <Route path="/collective/contract-templates" element={<ProtectedRoute><ContractTemplates /></ProtectedRoute>} />
          <Route path="/collective/tax-center" element={<ProtectedRoute><TaxDocumentCenter /></ProtectedRoute>} />
          <Route path="/agency/portal" element={<ProtectedRoute><AgencyPortal /></ProtectedRoute>} />
          <Route path="/compliance-checklist" element={<ComplianceChecklist />} />
          <Route path="/demo-workflow" element={<DemoWorkflow />} />
          <Route path="/workflow-docs" element={<WorkflowDocs />} />
          <Route path="/setup-demo" element={<SetupDemo />} />
          <Route path="/dashboard/ai-showcase" element={<ProtectedRoute><AIShowcase /></ProtectedRoute>} />
          <Route path="/dashboard/eligibility" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><Eligibility /></ProtectedRoute>} />
          <Route path="/dashboard/financial-aid" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><FinancialAid /></ProtectedRoute>} />
          <Route path="/dashboard/time-tracking" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><TimeTracking /></ProtectedRoute>} />
          <Route path="/dashboard/recruitment" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer", "athletic_director"]}><Recruitment /></ProtectedRoute>} />
          <Route path="/api-management" element={<ProtectedRoute><ApiManagement /></ProtectedRoute>} />
          <Route path="/webhook-management" element={<ProtectedRoute><WebhookManagement /></ProtectedRoute>} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/dashboard/nil-go-export" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><NILGoExport /></ProtectedRoute>} />
          <Route path="/athlete/tax-dashboard" element={<ProtectedRoute><TaxDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/admin/rule-changes" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><RuleChangeManagement /></ProtectedRoute>} />
          <Route path="/prospect/disclosure" element={<ProspectDisclosure />} />
          <Route path="/dashboard/fmv-market-rates" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><FMVMarketRates /></ProtectedRoute>} />
          <Route path="/dashboard/deal-pipeline" element={<ProtectedRoute><DealPipeline /></ProtectedRoute>} />
          <Route path="/dashboard/renewals" element={<ProtectedRoute><Renewals /></ProtectedRoute>} />
          <Route path="/dashboard/agent-marketplace" element={<ProtectedRoute><AgentMarketplace /></ProtectedRoute>} />
          <Route path="/dashboard/admin/agent-registry" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><AgentRegistry /></ProtectedRoute>} />
          <Route path="/dashboard/brand-marketplace" element={<ProtectedRoute><BrandMarketplace /></ProtectedRoute>} />
          <Route path="/athlete/monetization" element={<ProtectedRoute><MonetizationHub /></ProtectedRoute>} />
          <Route path="/dashboard/admin/conference-partnership" element={<ProtectedRoute requiredRoles={["super_admin"]}><ConferencePartnership /></ProtectedRoute>} />
          <Route path="/referral-partner-apply" element={<ReferralPartnerApplication />} />
          <Route path="/referral/portal" element={<ProtectedRoute><ReferralPartnerPortal /></ProtectedRoute>} />
          <Route path="/dashboard/admin/referral-partners" element={<ProtectedRoute requiredRoles={["super_admin"]}><ReferralPartnerManagement /></ProtectedRoute>} />
          <Route path="/dashboard/admin/referral-analytics" element={<ProtectedRoute requiredRoles={["super_admin"]}><ReferralAnalytics /></ProtectedRoute>} />
          <Route path="/dashboard/admin/integration-health" element={<ProtectedRoute requiredRoles={["super_admin", "compliance_officer"]}><IntegrationHealth /></ProtectedRoute>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
